import { Modal, Wizard, DataStoreContext, DataStore, Alert, Loader, requireDataStoreContext, requireStepContext, requireWizardContext, Flexbox } from 'st-app';
import { requireRoleDashboardContext } from '../RoleDashboardContext';
import { createEffect, createMemo, createSignal, onMount, Show } from 'solid-js';
import { StudentSelectionModal } from './StudentSelectionModal';
import { ICurrentClassInfo, studentSelect as stuApi } from 'sis-app/api'
import styles from '../sis-role-dashboard.module.css'

export function LocateStudentModal() {
    const ctx = requireRoleDashboardContext();
    const dataStore = new DataStoreContext(undefined, 'K12.Student');

    return <Modal show={[ctx.showLocateStudentModal, ctx.setShowLocateStudentModal]} title="Locate Student">
        <DataStore context={dataStore}>
            <Wizard onCancel={() => ctx.setShowLocateStudentModal(false)} onComplete={undefined}>
                <Wizard.Step title="Select Student">
                    <StudentSelectionStep />
                </Wizard.Step>
                <Wizard.Step title="Student Location">
                    <StudentLocationStep />
                </Wizard.Step>
            </Wizard>
        </DataStore>
    </Modal>;
}

export function StudentSelectionStep() {
    const ctx = requireWizardContext();
    const ctxStep = requireStepContext();
    const dataStore = requireDataStoreContext();

    onMount(() => {
        dataStore.setCurrentValue('K12-Student-StudentGU', '');
        ctxStep.setCompleted(false);

        createEffect(() => {
            const studentGU = dataStore.get('currentValue', 'K12-Student-StudentGU');
            if (studentGU) {
                ctxStep.next();
            }
        });
    });

    return <StudentSelectionModal hideFooter onClose={() => { }} />
}

export function StudentLocationStep() {
    const ctx = requireWizardContext();
    const ctxStep = requireStepContext();
    const dataStore = requireDataStoreContext();

    const studentGU = createMemo(() => dataStore.get('currentValue', 'K12-Student-StudentGU'));
    const stuFormattedName = createMemo(() => dataStore.get('currentValue', 'K12-Student-JoinByStudentGU-FormattedName', studentGU()));
    const ssyGU = createMemo(() => dataStore.get('currentValue', 'K12-EnrollmentInfo-StudentSOREnrollment-StudentSchoolYearGU', studentGU()));

    const [currentClass, setCurrentClass] = createSignal<ICurrentClassInfo>();
    createEffect(() => {
        const stuGU = studentGU();
        if (stuGU) {
            setCurrentClass(undefined);
            stuApi.GetCurrentClass(stuGU, ssyGU() ?? '').then(setCurrentClass);
        }
        else {
            setCurrentClass(undefined);
        }
    });

    return <div style="padding: 15px">
        <Show when={!currentClass()}>
            <Loader size="medium" />
        </Show>
        <Show when={currentClass()}>
            <strong>{stuFormattedName()}</strong>
            <Alert type="highlight">
                <CurrentClass classInfo={currentClass()!} />
            </Alert>
            <Alert type="info">
                <AttendanceInfo classInfo={currentClass()!} />
            </Alert>
        </Show>
    </div>
}

function AttendanceInfo(props: { classInfo: ICurrentClassInfo }) {
    const ci = props.classInfo;
    const description = createMemo(() => {
        if (ci.period) {
            return ci.periodAttCode ?? ci.allDayCode;
        }
        else {
            return ci.allDayCode;
        }
    });

    return <Show when={description()} fallback={<i>No Attendance Info</i>}>
        Attendance: <b>{description()}</b>
    </Show>
}

function CurrentClass(props: { classInfo: ICurrentClassInfo }) {
    function DataRow(props: { label: string, value: string | undefined }) {
        return <Show when={props.value}>
            <tr>
                <th scope="row">{props.label}:</th>
                <td>{props.value}</td>
            </tr>
        </Show>
    }

    const ci = props.classInfo;

    const description = createMemo(() => {
        switch (ci.studentLocationState) {
            case 0: //StudentLocation.CLASSES_NOT_STARTED:
                return 'First scheduled class is';
            case 2: //StudentLocation.IN_BETWEEN_CLASSES:
                return 'Between classes (Prev, Next):';
            case 3: //StudentLocation.ALL_CLASSES_OVER:
                return 'Last class was:';
            case 4: //StudentLocation.NO_CLASSES_TODAY:
                return 'For the current day, no Schedule Information was found for this student.';
            case 5: //StudentLocation.DAILY_BASED_ATTENDANCE:
                return 'Homeroom Information';
        }
        return '';
    });

    return <Flexbox dir="vertical" padded>
        <Flexbox.Item>{description()}</Flexbox.Item>
        <Flexbox.Item>
            <table class={styles['current-class']}>
                <DataRow label="Period" value={ci.formattedPeriod ?? ci.period} />
                <DataRow label="Room" value={ci.room} />
                <DataRow label="Section ID" value={ci.sectionID} />
                <DataRow label="Course Title" value={ci.courseTitle} />
                <DataRow label="Staff Name" value={ci.staffName} />
            </table>
        </Flexbox.Item>
    </Flexbox>
}
